import { sessionTimeoutConfigs } from '@shared/session'
import { AppId } from '@shared/types'
import { useSrPageFocusUpdate } from '@ui-common/hooks/useSrPageFocusUpdate'
import { setAuthorizationHeaderBearerToken, unsetAuthorizationHeaderBearerToken } from '@ui-common/utils/network'
import { UserContext } from '@ui-components/navigation/PrimaryTopBar/UserContext'
import axios from 'axios'
import { PropsWithChildren, Suspense, use, useEffect } from 'react'
import { AnyMachineSnapshot } from 'xstate'

import { AnalyticsService, CommonAnalyticsModalName, CustomAnalyticsEvent } from '../analytics'
import { GlobalServicesContext } from '../state'
import { CommonAnalyticsContext } from '../state/AnalyticsContext'
import { StepContext } from '../state/StepContext'
import { TransitionStep } from '../steps/TransitionStep/TransitionStep'
import { Modal } from './Modal'
import TimeoutWarningModal from './Modal/TimeoutWarningModal/TimeoutWarningModal'
import { useSessionTimer } from './Modal/TimeoutWarningModal/useSessionTimer'
import { StepErrorBoundary } from './StepErrorBoundary'

export interface CommonWizardWrapperProps {
  authToken: string | null
  step: string | AnyMachineSnapshot
  appId: AppId
  customAutoLogoutAnalyticsEvent?: CustomAnalyticsEvent
  onLogout: (isError: boolean) => unknown
  stepComponentMap: Record<string, () => React.ReactNode>
  userContext: UserContext | null
  analytics: {
    analyticsService: AnalyticsService<string, string, CommonAnalyticsModalName>
    analyticsPageName?: string
    shouldSkipPageViewEvents?: boolean
  }
}

const isAnyMachineSnapshot = (step: string | AnyMachineSnapshot): step is AnyMachineSnapshot =>
  typeof step === 'object' && !!step && 'value' in step

const getStepComponentKey = (
  stepComponentMap: Record<string, () => React.ReactNode>,
  step: string | AnyMachineSnapshot,
) => {
  let stepComponentKey: string | undefined
  if (isAnyMachineSnapshot(step)) {
    stepComponentKey = Object.keys(stepComponentMap).find((key) => step.matches(key))
  } else {
    stepComponentKey = step
  }

  return stepComponentKey
}

const getStepComponent = (
  stepComponentMap: Record<string, () => React.ReactNode>,
  step: string | AnyMachineSnapshot,
) => {
  const stepComponentKey = getStepComponentKey(stepComponentMap, step)

  const component = stepComponentKey ? stepComponentMap[stepComponentKey] : undefined
  return component ? component() : <TransitionStep isLoading={true} isErrored={false} error={undefined} />
}

export const CommonWizardWrapper = ({
  authToken,
  step,
  appId,
  customAutoLogoutAnalyticsEvent,
  onLogout,
  children,
  stepComponentMap,
  userContext,
  analytics,
}: PropsWithChildren<CommonWizardWrapperProps>) => {
  const globalServices = use(GlobalServicesContext)
  const { analyticsService, analyticsPageName, shouldSkipPageViewEvents } = analytics

  useSrPageFocusUpdate(step)

  if (authToken) {
    setAuthorizationHeaderBearerToken(axios, authToken)
  } else {
    unsetAuthorizationHeaderBearerToken(axios)
  }

  const isLoggedIn = !!authToken
  const sessionTimeoutConfig = sessionTimeoutConfigs[appId]
  const { shouldShowTimeoutModal, shouldLogout } = useSessionTimer(
    isLoggedIn,
    sessionTimeoutConfig.showTimeoutModalInMs,
    sessionTimeoutConfig.sessionLimitInMs,
  )

  useEffect(() => {
    if (isLoggedIn && shouldLogout) {
      if (customAutoLogoutAnalyticsEvent) {
        analyticsService.sendCustomEvent(customAutoLogoutAnalyticsEvent.category, customAutoLogoutAnalyticsEvent.name)
      }
      analyticsService.sendCustomEvent('TimeoutWarningModal', 'AutoLogoutTimeout')
      analyticsService.flushEvents()

      onLogout(false)
    }
  }, [isLoggedIn, shouldLogout, analyticsService, onLogout, customAutoLogoutAnalyticsEvent])

  const stepComponent = getStepComponent(stepComponentMap, step)
  const stepId = getStepComponentKey(stepComponentMap, step) || ''

  return (
    <StepErrorBoundary onExitClick={() => onLogout(true)}>
      <UserContext value={userContext ? { user: userContext.user, secondaryUser: userContext?.secondaryUser } : {}}>
        {children}
        <Suspense fallback={<TransitionStep isLoading={true} isErrored={false} error={undefined} />}>
          <StepContext value={{ stepId, analyticsPageName: analyticsPageName || null }}>
            <CommonAnalyticsContext value={{ analyticsService, shouldSkipPageViewEvents }}>
              {stepComponent}
            </CommonAnalyticsContext>
          </StepContext>
        </Suspense>
        {shouldShowTimeoutModal && (
          <TimeoutWarningModal
            analyticsService={analyticsService}
            onLogout={() => onLogout(false)}
            sessionTimeoutConfig={sessionTimeoutConfig}
          />
        )}
        <Modal service={globalServices.modalService} />
      </UserContext>
    </StepErrorBoundary>
  )
}
